import React, { useState, createContext, useContext, ReactNode, useEffect, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from "../providers/auth-context";
import { Button } from "@/components/ui/button";
import { parseISO, isToday, isYesterday, isWithinInterval, subDays } from 'date-fns';
import { Skeleton } from "@/components/ui/skeleton";
import { PanelLeftClose, PanelRightClose, SquarePen, Trash2 } from 'lucide-react';
import { useCanvas } from "@/components/ui/canvas/canvas";

// Updated to match the API response structure from chat history
interface ChatHistoryItem {
  chatId: string;
  userId: string;
  messages: { role: string; content: string }[];
  createdAt?: any; // Keep flexibility for Firestore timestamp object or ISO string
  lastUpdated?: any; // Keep flexibility for Firestore timestamp object or ISO string
}

// Helper function to categorize dates into relative time groups
const getRelativeTimeGroup = (date: Date): string => {
  const now = new Date();
  if (isNaN(date.getTime()) || date.getTime() === 0) return "Older"; // Handle invalid or epoch dates

  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  // Check intervals carefully - end date is exclusive by default in isWithinInterval
  if (isWithinInterval(date, { start: subDays(now, 7), end: subDays(now, 1) })) return "Previous 7 days";
  if (isWithinInterval(date, { start: subDays(now, 30), end: subDays(now, 7) })) return "Previous 30 days";
  return "Older";
};

// Define the desired order for display
const groupOrder = ["Today", "Yesterday", "Previous 7 days", "Previous 30 days", "Older"];

// Create context for sidebar state
interface SidebarContextType {
  isOpen: boolean;
  toggleSidebar: () => void;
  refreshChatHistory?: () => void;
  refreshKey: number; // Add refreshKey to context
}

export const SidebarContext = createContext<SidebarContextType>({
  isOpen: true,
  toggleSidebar: () => { },
  refreshChatHistory: undefined,
  refreshKey: 0,
});

export const useSidebar = () => useContext(SidebarContext);

interface SidebarProviderProps {
  children: ReactNode;
}

export const SidebarProvider = ({ children }: SidebarProviderProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const refreshChatHistory = () => setRefreshKey((k) => k + 1);

  return (
    <SidebarContext.Provider value={{ isOpen, toggleSidebar, refreshChatHistory, refreshKey }}>
      {children}
    </SidebarContext.Provider>
  );
};

interface ChatSidebarProps {
  activeChatId?: string; // Add prop for active chat ID
}

const ChatSidebar = ({ activeChatId }: ChatSidebarProps) => { // Add activeChatId prop
  const { isOpen, toggleSidebar, refreshChatHistory, refreshKey } = useSidebar();
  const router = useRouter();
  const { getIdToken, user } = useAuth();
  const [chatHistory, setChatHistory] = useState<ChatHistoryItem[]>([]);
  const [loading, setLoading] = useState(true); // Start loading initially
  const [error, setError] = useState<string | null>(null);
  const [deletingChatIds, setDeletingChatIds] = useState<Set<string>>(new Set());
  // Access canvas context
  const { isExpanded, setIsExpanded } = useCanvas();

  // Effect to coordinate sidebar and canvas states
  useEffect(() => {
    // If sidebar is opened, close the canvas
    if (isOpen && isExpanded) {
      setIsExpanded(false);
    }
  }, [isOpen, isExpanded, setIsExpanded]);

  // Custom toggle function to close canvas when opening sidebar
  const handleToggleSidebar = () => {
    // If we're going to open the sidebar, ensure canvas is closed
    if (!isOpen && isExpanded) {
      setIsExpanded(false);
    }
    // Perform the regular toggle
    toggleSidebar();
  };

  // Fetch chat history
  useEffect(() => {
    const fetchHistory = async () => {
      // No need to check isOpen here, fetch always if user exists
      if (!user) return;
      setLoading(true);
      setError(null);
      try {
        const token = await getIdToken();
        const res = await fetch("/api/chat-history", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to fetch chat history");
        const data = await res.json();

        // Extract chat history from the response and filter out memory chats
        const chats = (data.chatHistory || []).filter((chat: ChatHistoryItem) => !chat.chatId.startsWith('memory-'));
        setChatHistory(Array.isArray(chats) ? chats : []);
      } catch (err: any) {
        setError(err.message || "Unknown error");
      } finally {
        setLoading(false);
      }
    };
    fetchHistory(); // Fetch whenever user, getIdToken, or refreshKey changes
  }, [user, getIdToken, refreshKey]);

  const startNewChat = () => {
    // Generate a unique chat id (use crypto.randomUUID if available, else fallback)
    const id = typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : Date.now().toString();
    router.push(`/chat/${id}`);
    refreshChatHistory?.();
  };

  // Function to delete a chat
  const deleteChat = async (chatId: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent navigation to the chat
    
    if (!user || !chatId) return;

    try {
      // Add chatId to deletingChatIds to show loading state
      setDeletingChatIds(prev => new Set(prev).add(chatId));

      const token = await getIdToken();
      const res = await fetch(`/api/chat-history?chatId=${chatId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error(`Failed to delete chat: ${res.status}`);
      }

      // If currently viewing the deleted chat, redirect to main chat page
      if (chatId === activeChatId) {
        router.push('/chat');
      }

      // Refresh chat history after successful deletion
      refreshChatHistory?.();
    } catch (err: any) {
      console.error('Error deleting chat:', err);
      setError(`Failed to delete chat: ${err.message}`);
    } finally {
      // Remove chatId from deletingChatIds
      setDeletingChatIds(prev => {
        const updated = new Set(prev);
        updated.delete(chatId);
        return updated;
      });
    }
  };

  // Get the first user message from a chat for display
  const getFirstUserMessage = (chat: ChatHistoryItem): string => {
    if (!chat.messages || chat.messages.length === 0) {
      return "New conversation"; // Return default if no messages array
    }
    const firstUserMsg = chat.messages.find(msg => msg.role === 'user');
    if (firstUserMsg && firstUserMsg.content) { // Check content exists
      const contentPreview = firstUserMsg.content.split('\n')[0]; // Get first line
      return contentPreview.length > 50
        ? contentPreview.substring(0, 50) + '...'
        : contentPreview || "Chat started"; // Use preview or fallback
    }
    return "Conversation started"; // Fallback if no user message found
  };

  // Group chat history by relative time
  const groupedHistory = useMemo(() => {
    if (loading || error || !chatHistory || chatHistory.length === 0) return {};

    return chatHistory.reduce((acc, chat) => {
      let chatDate: Date;
      try {
        const dateInput = chat.lastUpdated || chat.createdAt;
        chatDate = dateInput?.seconds
          ? new Date(dateInput.seconds * 1000)
          : typeof dateInput === 'string' ? parseISO(dateInput) : new Date(0);
        if (isNaN(chatDate.getTime())) {
          chatDate = new Date(0);
        }
      } catch (e) {
        console.error("Error parsing chat date:", e, chat);
        chatDate = new Date(0);
      }

      const group = getRelativeTimeGroup(chatDate);
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(chat);
      // Sort chats within each group, newest first (handle potential invalid dates)
      acc[group].sort((a, b) => {
        const dateA = a.lastUpdated || a.createdAt;
        const dateB = b.lastUpdated || b.createdAt;
        const timeA = dateA?.seconds ? dateA.seconds * 1000 : (typeof dateA === 'string' ? parseISO(dateA).getTime() : 0);
        const timeB = dateB?.seconds ? dateB.seconds * 1000 : (typeof dateB === 'string' ? parseISO(dateB).getTime() : 0);
        // Fallback for potentially invalid times during sort
        const validTimeA = !isNaN(timeA) ? timeA : 0;
        const validTimeB = !isNaN(timeB) ? timeB : 0;
        return validTimeB - validTimeA;
      });
      return acc;
    }, {} as Record<string, ChatHistoryItem[]>);
  }, [chatHistory, loading, error]);


  return (
    <>
      {/* Sidebar toggle button - outside the sidebar when it's closed */}
      {!isOpen && (
        <Button
          variant="outline"
          size="icon"
          className="fixed top-20 left-4 z-50 transition-all duration-300 ease-in-out"
          onClick={handleToggleSidebar}
          aria-label="Expand sidebar"
        >
          <PanelRightClose className="h-4 w-4" />
        </Button>
      )}

      {/* Sidebar Content Area */}
      <div
        className={`fixed top-16 bottom-0 left-0 z-20 flex h-[calc(100vh-4rem)] w-[280px] flex-col transition-all duration-300 ease-in-out ${
          isOpen
            ? 'translate-x-0 opacity-100' // Slides in and fades in
            : '-translate-x-full opacity-0 pointer-events-none' // Slides out, fades out, becomes non-interactive
        }`}
        aria-hidden={!isOpen} // Improve accessibility
      >
        <div className="h-full flex flex-col bg-card rounded-lg border shadow-sm m-4" style={{ borderColor: 'hsl(var(--primary))' }}>
          {/* Sidebar Header with buttons */}
          <div className="flex items-center justify-between p-3 ">
            <div className="text-sm font-medium">Chat History</div>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={handleToggleSidebar}
                aria-label="Collapse sidebar"
              >
                <PanelLeftClose className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={startNewChat}
                aria-label="New chat"
              >
                <SquarePen className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Inner scrollable content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-2">
            {loading && (
              <div className="space-y-2 p-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            )}

            {error && (
              <div className="text-xs text-red-500 px-2 py-2">{error}</div>
            )}

            {!loading && !error && chatHistory.length === 0 && (
              <div className="text-sm text-muted-foreground px-2 py-4 text-center">
                No chats yet.
              </div>
            )}

            {!loading &&
              !error &&
              chatHistory.length > 0 &&
              Object.keys(groupedHistory).length === 0 && (
                <div className="text-sm text-muted-foreground px-2 py-4 text-center">
                  Processing history...
                </div>
              )}

            {!loading &&
              !error &&
              chatHistory.length > 0 &&
              Object.keys(groupedHistory).length > 0 &&
              groupOrder.map((groupName) => (
                groupedHistory[groupName] &&
                groupedHistory[groupName].length > 0 && (
                  <div key={groupName} className="mb-3">
                    <h3 className="text-xs font-semibold text-muted-foreground px-3 py-1.5 uppercase tracking-wider">
                      {groupName}
                    </h3>
                    <div className="space-y-1">
                      {groupedHistory[groupName].map((chat) => (
                        <div key={chat.chatId} className="relative group">
                          <Button
                            variant={
                              chat.chatId === activeChatId ? 'secondary' : 'ghost'
                            }
                            className="w-full h-auto justify-start text-left px-3 py-2 rounded-md transition-colors text-sm flex flex-col items-start gap-0.5 pr-8"
                            onClick={() => router.push(`/chat/${chat.chatId}`)}
                            disabled={deletingChatIds.has(chat.chatId)}
                          >
                            <span className="truncate block max-w-full font-medium leading-snug">
                              {getFirstUserMessage(chat)}
                            </span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => deleteChat(chat.chatId, e)}
                            disabled={deletingChatIds.has(chat.chatId)}
                            aria-label="Delete chat"
                          >
                            {deletingChatIds.has(chat.chatId) ? (
                              <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            ) : (
                              <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive transition-colors" />
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatSidebar;
